import type {
  Connector,
  Currency,
  EuclidComparison,
  EuclidComparisonType,
  EuclidIfStatement,
  EuclidValue,
  PaymentMethod,
  PlatformFeeKind,
  PlatformFeeOutput,
  PlatformFeeProgram,
  PlatformFeeRule,
} from './types';

/** A single condition leaf in the builder's condition tree. */
export interface LeafNode {
  kind: 'leaf';
  lhs: string;
  comparison: EuclidComparisonType;
  value: EuclidValue;
}

/** An AND (`all`) or OR (`any`) group of condition nodes. */
export interface GroupNode {
  kind: 'all' | 'any';
  children: ConditionNode[];
}

export type ConditionNode = LeafNode | GroupNode;

/**
 * A condition leaf. Numeric values (amount, merchant_volume) tag as `number`;
 * string values (payment_method, connector, currency, card_network) tag as
 * `enum_variant`.
 */
export function leaf(
  lhs: string,
  comparison: EuclidComparisonType,
  value: string | number,
): LeafNode {
  const tagged: EuclidValue =
    typeof value === 'number' ? { type: 'number', value } : { type: 'enum_variant', value };
  return { kind: 'leaf', lhs, comparison, value: tagged };
}

/** AND group — all children must match. */
export function allOf(...children: ConditionNode[]): GroupNode {
  return { kind: 'all', children };
}

/** OR group — any child matching is enough. */
export function anyOf(...children: ConditionNode[]): GroupNode {
  return { kind: 'any', children };
}

/**
 * How a rule (or the default) prices a transaction. `fee_type` is inferred:
 * percentage-only → `percentage`, flat-only → `flat`, both → `combined`.
 */
export interface FeeSpecInput {
  /** Percentage fee, e.g. `2.5` means 2.5%. */
  percentage?: number;
  /** Flat fee in minor units. */
  flat?: number;
  /** ISO 4217 currency for the flat fee. */
  flatCurrency?: string;
  /** Clamp floor in minor units. */
  min?: number;
  /** Clamp ceiling in minor units. */
  max?: number;
}

/**
 * Friendly conditions for a rule. Every provided key becomes one condition and
 * they are ANDed together. For dimensions not covered here (payment-method-type
 * keys like `crypto`/`wallet`, metadata, value arrays) use `rawConditions`.
 */
export interface FeeRuleConditions {
  paymentMethod?: PaymentMethod;
  connector?: Connector;
  currency?: Currency;
  cardNetwork?: string;
  /**
   * Customer billing-address country. Must be the exact backend `Country` enum
   * variant (PascalCase full name, e.g. `Germany`/`UnitedStatesOfAmerica`), not
   * an ISO code — the engine lowers `billing_country` via case-sensitive
   * `from_str`.
   */
  billingCountry?: string;
  /** `amount == n` (minor units). */
  amountEquals?: number;
  /** `amount > n` (minor units). */
  amountGreaterThan?: number;
  /** `amount < n` (minor units). */
  amountLessThan?: number;
  /**
   * `merchant_volume == n` — the merchant's previous-month volume snapshot
   * (USD minor units). Combine with any other condition, e.g.
   * `{ paymentMethod: 'crypto', merchantVolumeGreaterThan: 1_000_000 }`.
   */
  merchantVolumeEquals?: number;
  /** `merchant_volume > n` (USD minor units). */
  merchantVolumeGreaterThan?: number;
  /** `merchant_volume < n` (USD minor units). */
  merchantVolumeLessThan?: number;
}

export interface FeeRuleInput {
  name: string;
  /** Friendly conditions (ANDed). Omit for an always-matching rule (prefer `otherwise`). */
  when?: FeeRuleConditions;
  /** Extra raw conditions ANDed in, for dimensions `when` does not cover. */
  rawConditions?: EuclidComparison[];
  /** Nested AND/OR condition tree. Mutually exclusive with `when`/`rawConditions`. */
  match?: ConditionNode;
  fee: FeeSpecInput;
}

function toFeeOutput(spec: FeeSpecInput): PlatformFeeOutput {
  const hasPct = spec.percentage != null;
  const hasFlat = spec.flat != null;
  const feeType: PlatformFeeKind = hasPct && hasFlat ? 'combined' : hasFlat ? 'flat' : 'percentage';
  return {
    fee_type: feeType,
    percentage_fee: spec.percentage ?? null,
    flat_fee_amount: spec.flat ?? null,
    flat_fee_currency: spec.flatCurrency ?? null,
    min_fee_amount: spec.min ?? null,
    max_fee_amount: spec.max ?? null,
  };
}

function enumCondition(lhs: string, value: string): EuclidComparison {
  return { lhs, comparison: 'equal', value: { type: 'enum_variant', value }, metadata: {} };
}

function numberCondition(
  lhs: string,
  comparison: EuclidComparisonType,
  value: number,
): EuclidComparison {
  return { lhs, comparison, value: { type: 'number', value }, metadata: {} };
}

function buildConditions(
  when: FeeRuleConditions = {},
  raw: EuclidComparison[] = [],
): EuclidComparison[] {
  const out: EuclidComparison[] = [];
  if (when.paymentMethod != null) out.push(enumCondition('payment_method', when.paymentMethod));
  if (when.connector != null) out.push(enumCondition('connector', when.connector));
  if (when.currency != null) out.push(enumCondition('currency', when.currency));
  if (when.cardNetwork != null) out.push(enumCondition('card_network', when.cardNetwork));
  if (when.billingCountry != null) out.push(enumCondition('billing_country', when.billingCountry));
  if (when.amountEquals != null) out.push(numberCondition('amount', 'equal', when.amountEquals));
  if (when.amountGreaterThan != null) {
    out.push(numberCondition('amount', 'greater_than', when.amountGreaterThan));
  }
  if (when.amountLessThan != null) {
    out.push(numberCondition('amount', 'less_than', when.amountLessThan));
  }
  if (when.merchantVolumeEquals != null) {
    out.push(numberCondition('merchant_volume', 'equal', when.merchantVolumeEquals));
  }
  if (when.merchantVolumeGreaterThan != null) {
    out.push(numberCondition('merchant_volume', 'greater_than', when.merchantVolumeGreaterThan));
  }
  if (when.merchantVolumeLessThan != null) {
    out.push(numberCondition('merchant_volume', 'less_than', when.merchantVolumeLessThan));
  }
  out.push(...raw);
  return out;
}

function leafToComparison(node: LeafNode): EuclidComparison {
  return { lhs: node.lhs, comparison: node.comparison, value: node.value, metadata: {} };
}

/**
 * Flatten associativity (nested all-in-all / any-in-any) and collapse
 * single-child groups, so every group child of an `all` is an `any`.
 * @internal
 */
export function normalizeNode(node: ConditionNode): ConditionNode {
  if (node.kind === 'leaf') return node;
  const children = node.children.map(normalizeNode);
  const flat: ConditionNode[] = [];
  for (const c of children) {
    if (c.kind === node.kind) flat.push(...c.children);
    else flat.push(c);
  }
  if (flat.length === 1) return flat[0];
  return { kind: node.kind, children: flat };
}

function toStatement(node: ConditionNode): EuclidIfStatement {
  if (node.kind === 'leaf') return { condition: [leafToComparison(node)], nested: null };
  // Unreachable via current callers (encodeStatements strips top-level any); kept for totality.
  if (node.kind === 'any') {
    return { condition: [], nested: node.children.map(toStatement) };
  }
  // 'all': after normalize, group children are all 'any'.
  const leaves = node.children.filter((c): c is LeafNode => c.kind === 'leaf');
  const groups = node.children.filter((c): c is GroupNode => c.kind !== 'leaf');
  const condition = leaves.map(leafToComparison);
  if (groups.length === 0) return { condition, nested: null };
  const [first, ...rest] = groups;
  // `first` is an OR; AND each of its branches with the remaining OR groups.
  const nested = first.children.map((branch) => toStatement(normalizeNode(allOf(branch, ...rest))));
  return { condition, nested };
}

function assertNoEmptyAnyOf(node: ConditionNode): void {
  if (node.kind === 'leaf') return;
  if (node.kind === 'any' && node.children.length === 0) {
    throw new Error('feeProgram: an anyOf() group must have at least one condition');
  }
  node.children.forEach(assertNoEmptyAnyOf);
}

/**
 * Encode a rule's match (a condition tree) into the engine's `statements[]`.
 * A top-level OR spreads across statements; anything else is a single statement.
 * @internal
 */
export function encodeStatements(match: ConditionNode): EuclidIfStatement[] {
  const m = normalizeNode(match);
  if (m.kind === 'any') return m.children.map(toStatement);
  return [toStatement(m)];
}

function comparisonToLeaf(c: EuclidComparison): LeafNode {
  return { kind: 'leaf', lhs: c.lhs, comparison: c.comparison, value: c.value };
}

function statementToNode(stmt: EuclidIfStatement): ConditionNode {
  const leaves = stmt.condition.map(comparisonToLeaf);
  if (stmt.nested && stmt.nested.length > 0) {
    const orNode = anyOf(...stmt.nested.map(statementToNode));
    if (leaves.length === 0) return orNode;
    return allOf(...leaves, orNode);
  }
  return leaves.length === 1 ? leaves[0] : allOf(...leaves);
}

/**
 * Decode a rule's `statements[]` back into a condition tree.
 *
 * Returns a tree that is **logically equivalent** to the source. It is
 * deep-equal to `normalizeNode(input)` only when no `all` group contains two
 * or more `any` groups; where the encoder distributed AND over OR, the decoded
 * shape differs (still equivalent).
 */
export function ruleMatchToTree(statements: EuclidIfStatement[]): ConditionNode {
  if (statements.length === 1) return normalizeNode(statementToNode(statements[0]));
  return normalizeNode(anyOf(...statements.map(statementToNode)));
}

/**
 * Decode a stored program into the builder's editable model.
 *
 * Returns a tree that is **logically equivalent** to the source. It is
 * deep-equal to `normalizeNode(input)` only when no `all` group contains two
 * or more `any` groups; where the encoder distributed AND over OR, the decoded
 * shape differs (still equivalent).
 */
export function programToTree(program: PlatformFeeProgram): {
  rules: { name: string; match: ConditionNode; fee: PlatformFeeOutput | null }[];
  otherwise: PlatformFeeOutput | null;
} {
  return {
    rules: program.rules.map((r) => ({
      name: r.name,
      match: ruleMatchToTree(r.statements),
      fee: r.connectorSelection.fee ?? null,
    })),
    otherwise: program.defaultSelection.fee ?? null,
  };
}

/**
 * Fluent builder for a platform fee-rule program. Emits the exact Euclid wire
 * shape (camelCase tree, tagged values, `metadata: {}` everywhere) so callers
 * never hand-write the AST. Rules are evaluated in order; the first match wins,
 * else `otherwise` (the default selection).
 *
 * @example
 * ```ts
 * const algorithm = feeProgram()
 *   .rule({ name: 'crypto', when: { paymentMethod: 'crypto' }, fee: { percentage: 1.0 } })
 *   .rule({
 *     name: 'card_on_cryptomus',
 *     when: { paymentMethod: 'card', connector: 'cryptomus' },
 *     fee: { percentage: 2.0 },
 *   })
 *   .otherwise({ percentage: 3.0 })
 *   .build();
 *
 * await delopay.fees.rules.upsert({ algorithm }, merchantId);
 * ```
 */
export class FeeProgramBuilder {
  private readonly rules: PlatformFeeRule[] = [];
  private defaultFee: PlatformFeeOutput | null = null;

  /** Append a rule. Provided `when`/`rawConditions` are ANDed. */
  rule(input: FeeRuleInput): this {
    if (input.match) assertNoEmptyAnyOf(input.match);
    const statements: PlatformFeeRule['statements'] = input.match
      ? encodeStatements(input.match)
      : [{ condition: buildConditions(input.when, input.rawConditions) }];
    this.rules.push({
      name: input.name,
      connectorSelection: { fee: toFeeOutput(input.fee) },
      statements,
    });
    return this;
  }

  /** Set the default selection (applied when no rule matches). */
  otherwise(fee: FeeSpecInput): this {
    this.defaultFee = toFeeOutput(fee);
    return this;
  }

  /** Produce the wire-ready program. */
  build(): PlatformFeeProgram {
    return {
      defaultSelection: { fee: this.defaultFee },
      rules: this.rules,
      metadata: {},
    };
  }
}

/** Start building a platform fee-rule program. See {@link FeeProgramBuilder}. */
export function feeProgram(): FeeProgramBuilder {
  return new FeeProgramBuilder();
}
